import { useState, useEffect } from 'react'
import { siteAPI, uploadFile, uploadVideo } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl } from '../utils/helpers'

const Settings = () => {
  const { showSuccess, showError } = useNotification()
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await siteAPI.getSettings()
      setSettings(response.data.settings || {})
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleTextChange = (e) => {
    const { name, value } = e.target
    setSettings(prev => ({ ...prev, [name]: value }))
  }

  const handleSettingsSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const formData = new FormData(e.target)
    
    try {
      const data = Object.fromEntries(formData.entries())
      await siteAPI.updateSettings(data)
      showSuccess('Settings saved successfully.')
      await fetchSettings()
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = async (e, target) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const imageFile = formData.get('image')
    const remove = formData.get('remove') === '1'

    setUploadingLogo(true)
    try {
      if (remove) {
        await siteAPI.updateSettings({ [target]: '' })
        showSuccess(`${target === 'logo' ? 'Logo' : 'Image'} removed.`)
      } else if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        const uploadedFilename = uploadRes.data.filename || uploadRes.data.image
        if (!uploadedFilename) throw new Error('No filename returned from upload.')
        await siteAPI.updateSettings({ [target]: uploadedFilename })
        showSuccess(`${target === 'logo' ? 'Logo' : 'Image'} updated.`)
      } else {
        showError('Choose an image file to upload.')
        setUploadingLogo(false)
        return
      }
      await fetchSettings()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleVideoUpload = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const videoFile = formData.get('video')
    const remove = formData.get('remove') === '1'

    setUploadingVideo(true)
    try {
      if (remove) {
        await siteAPI.updateSettings({ hero_video: '' })
        showSuccess('Homepage video removed.')
      } else if (videoFile && videoFile.size > 0) {
        const uploadRes = await uploadVideo(videoFile)
        const uploadedFilename = uploadRes.data.filename || uploadRes.data.video
        if (!uploadedFilename) throw new Error('No filename returned from video upload.')
        await siteAPI.updateSettings({ hero_video: uploadedFilename })
        showSuccess('Homepage video updated.')
      } else {
        showError('Choose a video file to upload.')
        setUploadingVideo(false)
        return
      }
      await fetchSettings()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setUploadingVideo(false)
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">Website Content &amp; Settings</h2>
      <p className="admin-sub">Manage global salon information, contact details, social links and media.</p>

      <div className="panel">
        <div className="panel-head">
          <i className="fa fa-info-circle" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
          Business &amp; Site Information
        </div>
        <form onSubmit={handleSettingsSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Studio / Business Name</label>
                <input
                  type="text"
                  name="site_name"
                  className="form-control"
                  value={settings.site_name || ''}
                  onChange={handleTextChange}
                  placeholder="HEET7 Studio"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Tagline</label>
                <input
                  type="text"
                  name="tagline"
                  className="form-control"
                  value={settings.tagline || ''}
                  onChange={handleTextChange}
                  placeholder="Beauty Studio"
                />
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="text"
                  name="phone"
                  className="form-control"
                  value={settings.phone || ''}
                  onChange={handleTextChange}
                  placeholder="+01 123 456 7890"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={settings.email || ''}
                  onChange={handleTextChange}
                  placeholder="hello@heet7studio.com"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  className="form-control"
                  value={settings.address || ''}
                  onChange={handleTextChange}
                  placeholder="515 West Ave, Norwalk"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Opening Hours</label>
                <input
                  type="text"
                  name="hours"
                  className="form-control"
                  value={settings.hours || ''}
                  onChange={handleTextChange}
                  placeholder="Mon–Sun : 9:00 – 20:00"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>WhatsApp Number (e.g. 919876543210)</label>
                <input
                  type="text"
                  name="whatsapp_number"
                  className="form-control"
                  value={settings.whatsapp_number || ''}
                  onChange={handleTextChange}
                  placeholder="WhatsApp number with country code"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Footer Copyright Text</label>
                <input
                  type="text"
                  name="copyright_text"
                  className="form-control"
                  value={settings.copyright_text || ''}
                  onChange={handleTextChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label>Facebook URL</label>
                <input
                  type="url"
                  name="social_facebook"
                  className="form-control"
                  value={settings.social_facebook || ''}
                  onChange={handleTextChange}
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Instagram URL</label>
                <input
                  type="url"
                  name="social_instagram"
                  className="form-control"
                  value={settings.social_instagram || ''}
                  onChange={handleTextChange}
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Twitter / X URL</label>
                <input
                  type="url"
                  name="social_twitter"
                  className="form-control"
                  value={settings.social_twitter || ''}
                  onChange={handleTextChange}
                  placeholder="https://x.com/..."
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>LinkedIn URL</label>
                <input
                  type="url"
                  name="social_linkedin"
                  className="form-control"
                  value={settings.social_linkedin || ''}
                  onChange={handleTextChange}
                  placeholder="https://linkedin.com/..."
                />
              </div>
            </div>
          </div>

          <button className="btn btn-gold btn-lg" disabled={saving}>
            <i className="fa fa-save"></i> {saving ? 'Saving Settings...' : 'Save Settings'}
          </button>
        </form>
      </div>

      <div className="row mt-4">
        <div className="col-md-6">
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-picture-o" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Website Logo
            </div>
            {settings.logo && (
              <div className="file-preview" style={{textAlign: 'center', marginBottom: '12px'}}>
                <span style={{display: 'block', fontSize: '12px', color: 'var(--muted)', marginBottom: '6px'}}>Current Logo</span>
                <img src={getImageUrl(settings.logo)} alt="Logo" style={{maxHeight: '70px', objectFit: 'contain'}} />
              </div>
            )}
            <form onSubmit={(e) => handleImageUpload(e, 'logo')}>
              <div className="form-group">
                <input type="file" name="image" className="form-control-file" accept="image/*" />
              </div>
              {settings.logo && (
                <div className="form-group mb-2">
                  <label className="checkbox-inline">
                    <input type="checkbox" name="remove" value="1" /> Remove current logo
                  </label>
                </div>
              )}
              <button className="btn btn-gold btn-sm mt-2" disabled={uploadingLogo}>
                <i className={`fa ${uploadingLogo ? 'fa-spinner fa-spin' : 'fa-cloud-upload'}`}></i>
                {' '}{uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </button>
            </form>
          </div>
        </div>

        <div className="col-md-6">
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-video-camera" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Homepage Video Showcase
            </div>
            {settings.hero_video && (
              <div className="file-preview" style={{marginBottom: '12px'}}>
                <span style={{display: 'block', fontSize: '12px', color: 'var(--muted)', marginBottom: '6px'}}>Current Video</span>
                <video controls style={{width: '100%', maxHeight: '180px', borderRadius: '8px'}}>
                  <source src={getImageUrl(settings.hero_video)} />
                </video>
              </div>
            )}
            <form onSubmit={handleVideoUpload}>
              <div className="form-group">
                <input type="file" name="video" className="form-control-file" accept="video/*" />
              </div>
              {settings.hero_video && (
                <div className="form-group mb-2">
                  <label className="checkbox-inline">
                    <input type="checkbox" name="remove" value="1" /> Remove current video
                  </label>
                </div>
              )}
              <button className="btn btn-gold btn-sm mt-2" disabled={uploadingVideo}>
                <i className={`fa ${uploadingVideo ? 'fa-spinner fa-spin' : 'fa-cloud-upload'}`}></i>
                {' '}{uploadingVideo ? 'Uploading...' : 'Upload Video'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings
